import {
  Amount,
  Box,
  Button,
  Card,
  CardBody,
  DotIcon,
  MinusCircleIcon,
  SparklesIcon,
  Text,
  UserIcon,
} from '@razorpay/blade/components';
import { bladeTheme } from '@razorpay/blade/tokens';
import { routePaths } from 'components/Routes/data';
import { DonutChart } from 'components/ui/DonutChart';
import { useSearchParams } from 'hooks/useSearchParams';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { usePayDetails } from '../context/PayDetailsContext';
import { SummaryCard } from './SummaryCard';
import IsFeatureDisabledForRearch from 'access/IsFeatureDisabledForRearch';
import useHasPermission from 'utils/useHasPermission';
import permissions from 'constants/permissions';
import useVPF from '../hooks/VPF/useVPF';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import { useQuery } from 'react-query';
import { generateUserId } from 'utils/commons';
import {
  listPerquisiteComponentAssignments,
  PayrollListPerquisiteComponentAssignmentsResponse,
} from 'api/gql-utils/perquisitesAssignments';
import useReduxSelector from 'utils/useReduxSelector';
import { paiseAmountToRupee } from 'utils/Numbers';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';

export const SummarySection = () => {
  const { isM3S1Enabled } = useIsFeatureDisabledForRearch();
  const { compensationData } = usePayDetails();
  const { vpfNode } = useVPF();
  let organizationId = `org_${useReduxSelector(
    (state) => state?.loggedInUser?.data?.currentOrganization?.id || '',
  )}`;
  const summaryData = compensationData.summaryCardData;
  const totalDeductions =
    summaryData.totalRecurringDeductionAmount + (isM3S1Enabled ? vpfNode.monthlyAmount : 0);
  const hasPayrollEditPermission = useHasPermission([permissions.PAYROLL_EDIT]);
  const history = useHistory();
  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId);
  const userId = useSearchParams().urlParams.get('userId') || loggedInUserId;
  const handleReviseCtcClick = () => {
    history.push({
      pathname: routePaths.payDetails.compensation.edit.salaryDetails,
      search: userId ? `?userId=${userId}` : '',
    });
  };
  const { data: totalPerquisitesAmount } = useQuery({
    queryKey: [listPerquisiteComponentAssignments, userId],
    queryFn: () =>
      listPerquisiteComponentAssignments({
        input: {
          userIds: [generateUserId(userId || '')],
          merchantId: organizationId,
          tenantId: organizationId,
        },
      }),
    select: (data: PayrollListPerquisiteComponentAssignmentsResponse) => {
      return data.payrollListPerquisiteComponentAssignments.data.reduce(
        (acc, item) => acc + paiseAmountToRupee(parseFloat(item.totalAmount)),
        0,
      );
    },
    enabled: !!userId && isM3S1Enabled,
  });

  return (
    <Box display={'flex'} flexDirection={'column'} gap={'spacing.5'}>
      <Card padding="spacing.7" borderRadius="large">
        <CardBody>
          <Box
            display={'flex'}
            flexDirection={{ base: 'column', m: 'row' }}
            justifyContent={'space-between'}
            gap={'48px'}>
            <Box
              alignItems={'center'}
              width={'100px'}
              height={'170px'}
              marginLeft={{ base: '60px', m: 'spacing.8' }}>
              <DonutChart
                data={[
                  {
                    percentage: summaryData.fixedPercentage,
                    color: bladeTheme.colors.onDark.interactive.icon.positive.subtle,
                  }, // Green - Fixed component
                  {
                    percentage: summaryData.variablePercentage,
                    color: bladeTheme.colors.onDark.interactive.icon.information.normal,
                  }, // Blue - Variable component
                  {
                    percentage: summaryData.otherCtcPercentage,
                    color: bladeTheme.colors.onDark.interactive.icon.notice.normal,
                  }, // Orange - Other CTC component
                ]}
                size={170}
                strokeWidth={20}
              />
            </Box>
            <Box
              minWidth={{ base: 'auto', m: '458px' }}
              display={'flex'}
              flexDirection={'column'}
              gap={'spacing.7'}>
              <Box
                display={'flex'}
                flexDirection={{ base: 'column', m: 'row', xs: 'initial' }}
                gap={{ base: 'spacing.4' }}
                justifyContent={'space-between'}>
                <Box display={'flex'} flexDirection={'column'}>
                  <Text weight="medium" color={'interactive.text.gray.muted'}>
                    Annual CTC
                  </Text>
                  <Amount
                    type={'heading'}
                    size="2xlarge"
                    weight="semibold"
                    value={summaryData.annualCtc}
                    currency="INR"
                  />
                </Box>
                {hasPayrollEditPermission ? (
                  <Box width={'118px'}>
                    <Button variant={'primary'} isFullWidth onClick={handleReviseCtcClick}>
                      Revise CTC
                    </Button>
                  </Box>
                ) : null}
              </Box>
              <Box
                display={'flex'}
                flexDirection={'column'}
                width={'100%'}
                justifyContent={'space-between'}
                gap={'spacing.3'}>
                <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
                  <Box
                    display={'flex'}
                    flexDirection={'row'}
                    gap={'spacing.2'}
                    alignItems={'center'}>
                    <DotIcon size="small" color={'interactive.icon.positive.subtle'} />
                    <Text color={'surface.text.gray.muted'}>Fixed</Text>
                  </Box>
                  <Amount
                    type={'heading'}
                    size="small"
                    weight="semibold"
                    value={summaryData.totalFixedValue}
                  />
                </Box>
                <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
                  <Box
                    display={'flex'}
                    flexDirection={'row'}
                    gap={'spacing.2'}
                    alignItems={'center'}>
                    <DotIcon size="small" color={'interactive.icon.information.normal'} />
                    <Text color={'surface.text.gray.muted'}>Variable</Text>
                  </Box>
                  <Amount
                    type={'heading'}
                    size="small"
                    weight="semibold"
                    value={summaryData.totalVariableValue}
                  />
                </Box>
                <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
                  <Box
                    display={'flex'}
                    flexDirection={'row'}
                    gap={'spacing.2'}
                    alignItems={'center'}>
                    <DotIcon size="small" color={'interactive.icon.notice.normal'} />
                    <Text color={'surface.text.gray.muted'}>Other CTC component</Text>
                  </Box>
                  <Amount
                    type={'heading'}
                    size="small"
                    weight="semibold"
                    value={summaryData.totalOtherCtcValue}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </CardBody>
      </Card>
      <Box display="flex" flexDirection={{ base: 'column', m: 'row' }} gap={'spacing.5'}>
        <IsFeatureDisabledForRearch feature={['isRecurringDeductionsDisabled']}>
          <SummaryCard
            icon={<MinusCircleIcon size="medium" />}
            title="Deductions"
            amount={totalDeductions}
          />
        </IsFeatureDisabledForRearch>
        <IsFeatureDisabledForRearch feature={['isPerquisiteDisabled']}>
          <SummaryCard
            icon={<SparklesIcon size="medium" />}
            title="Perquisites"
            amount={totalPerquisitesAmount || 0}
          />
        </IsFeatureDisabledForRearch>
        <IsFeatureDisabledForRearch feature={['isFBPDisabled']}>
          <SummaryCard icon={<UserIcon size="medium" />} title="FBP allocated" amount={3000} />
        </IsFeatureDisabledForRearch>
      </Box>
    </Box>
  );
};
